import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useDevice } from "@/lib/useDevice";

/**
 * AdSlot — public renderer for an advertising slot.
 *
 * Props:
 *  - slot: string (slot slug)
 *  - category: optional string (category slug — for /categoria/:slug pages)
 *  - articleId: optional string (for article pages)
 *  - className: additional wrapper classes
 *  - floating: bool — render as fixed floating banner (with dismiss)
 *
 * If no creative is available, renders nothing (no empty box, no "Publicidad" label).
 */
export default function AdSlot({ slot, category, articleId, className = "", floating = false, showPlaceholder = false, placeholderLabel = "Espacio publicitario", placeholderClassName = "" }) {
  const device = useDevice();
  const [creative, setCreative] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const impressionSentRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    setCreative(null);
    impressionSentRef.current = false;

    api.serveAd(slot, device, category, articleId)
      .then((r) => {
        if (!alive) return;
        setCreative(r?.creative || null);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) { setCreative(null); setLoaded(true); }
      });

    return () => { alive = false; };
  }, [slot, device, category, articleId]);

  // Log impression once when creative is set and mounted
  useEffect(() => {
    if (!creative || impressionSentRef.current) return;
    impressionSentRef.current = true;
    api.logAdEvent(creative.id, "impression", device);
  }, [creative, device]);

  if (!loaded) return null;
  if ((!creative || dismissed) && !showPlaceholder) return null;
  if ((!creative || dismissed) && showPlaceholder) {
    return (
      <div
        data-ad-slot={slot}
        data-ad-placeholder="true"
        className={`ad-slot ad-placeholder ${placeholderClassName || className}`}
      >
        <div className="w-full h-full flex flex-col items-center justify-center text-textgray/70 border border-dashed border-softblue bg-lightgray/50 p-4 text-center">
          <span className="text-[10px] uppercase tracking-widest font-semibold">{placeholderLabel}</span>
          <span className="text-[10px] mt-1 opacity-70">slot: {slot}</span>
        </div>
      </div>
    );
  }

  const handleClick = () => {
    api.logAdEvent(creative.id, "click", device);
  };

  const targetUrl = creative.target_url;

  const inner = (() => {
    if (creative.creative_type === "html" || creative.creative_type === "embed") {
      return (
        <div
          className="ad-html-container w-full max-w-full overflow-hidden"
          dangerouslySetInnerHTML={{ __html: creative.html_code || "" }}
        />
      );
    }
    if (creative.creative_type === "video") {
      return (
        <video
          src={creative.asset_url}
          className="w-full h-auto max-w-full block"
          autoPlay
          muted
          loop
          playsInline
        />
      );
    }
    // image / gif — use <img>, preserving natural aspect (no crop)
    return (
      <img
        src={creative.asset_url}
        alt={creative.alt || ""}
        loading="lazy"
        className="block w-full h-auto max-w-full"
      />
    );
  })();

  const clickable = !!targetUrl && creative.creative_type !== "html" && creative.creative_type !== "embed";

  if (floating) {
    return (
      <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[95vw] shadow-2xl ${className}`}>
        <div className="relative bg-white border border-softblue">
          <button
            onClick={() => setDismissed(true)}
            aria-label="Cerrar"
            className="absolute -top-3 -right-3 h-7 w-7 bg-white border border-softblue rounded-full inline-flex items-center justify-center text-nightblue hover:bg-lightgray z-10"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          {clickable ? (
            <a href={targetUrl} onClick={handleClick} target="_blank" rel="noopener noreferrer sponsored">
              {inner}
            </a>
          ) : (
            <div onClick={targetUrl ? handleClick : undefined}>{inner}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-ad-slot={slot} className={`ad-slot ${className}`}>
      {clickable ? (
        <a href={targetUrl} onClick={handleClick} target="_blank" rel="noopener noreferrer sponsored" className="block">
          {inner}
        </a>
      ) : (
        <div onClick={targetUrl ? handleClick : undefined}>{inner}</div>
      )}
    </div>
  );
}
